import { useState, useMemo, useEffect } from "react";
import {
  Card,
  Select,
  Input,
  Button,
  Radio,
  Space,
  message,
  Divider,
  Pagination,
  Tag,
} from "antd";
import { PlusOutlined, SaveOutlined } from "@ant-design/icons";
import DeleteModal from "../../components/models/DeleteModal";
import ConfirmModal from "../../components/models/ConfirmModal";
import { useApiCall } from "../../services/api";
import { useSelector } from "react-redux";
import useExpiredToken from "../../utils/useExpiredToken";
import { useRef } from "react";

const { TextArea } = Input;


const QUESTION_TYPES = [
  { label: "MCQ", value: "MCQ" },
  { label: "Fill in the Blank", value: "FILL_BLANK" },
  { label: "Short Form Answer", value: "Short_Form_Answer" },
];

const getQuestionTypeLabel = (value) =>
  QUESTION_TYPES.find((t) => t.value === value)?.label || value;

const CATEGORIES = [
  "Product",
  "Quality",
  "Process",
  "Dispositions",
  "COC",
  "Call Etiquettes",
  "Synapse",
  "POSH",
  "Information Security",
];

const DEFAULT_OPTIONS = [
  { option_text: "", is_correct: false },
  { option_text: "", is_correct: false },
];

const PAGE_SIZE = 10;

const PktQuestionnaire = () => {
  const [editIndex, setEditIndex] = useState(null);
  const [questionType, setQuestionType] = useState(null);
  const [category, setCategory] = useState(null);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(DEFAULT_OPTIONS);

  const [draftQuestions, setDraftQuestions] = useState([]);
  const [savedQuestions, setSavedQuestions] = useState([]);

  const [filterQuestionType, setFilterQuestionType] = useState(null);
  const [filterCategory, setFilterCategory] = useState(null);

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deleteIndex, setDeleteIndex] = useState(null);
  const [deleteFrom, setDeleteFrom] = useState(null);
  const [currentEditingSavedIndex, setCurrentEditingSavedIndex] = useState(null);
  const [isExcelConfirmOpen, setIsExcelConfirmOpen] = useState(false);
const [selectedExcelFile, setSelectedExcelFile] = useState(null);

  
  const [currentPage, setCurrentPage] = useState(1);

  /* ---------------- API & TOKEN ---------------- */
  const { callApi, loading } = useApiCall();
  const expireToken = useExpiredToken();
  const token = useSelector((state) => state.auth.jwtToken);
  const API_URL = "superadmin/master/handle-pkt-questions"; 

  /* ---------------- MCQ HELPERS ---------------- */
  const addOption = () =>
    setOptions([...options, { option_text: "", is_correct: false }]);

  const updateOptionText = (index, value) => {
    const updated = [...options];
    updated[index].option_text = value;
    setOptions(updated);
  };

  const setCorrectOption = (index) => {
    setOptions(options.map((o, i) => ({ ...o, is_correct: i === index })));
  };

  const validateMCQ = () => {
    const filled = options.filter((o) => o.option_text.trim());
    const correct = options.filter((o) => o.is_correct).length;

    if (filled.length < 2) {
      message.error("MCQ must have at least 2 options");
      return false;
    }
    if (correct !== 1) {
      message.error("Select exactly one correct answer");
      return false;
    }
    return true;
  };

  /* ---------------- ADD / UPDATE DRAFT ---------------- */
  const handleAddDraft = async () => {
  if (!questionType || !category || !question.trim()) {
    message.error("Please fill all required fields");
    return;
  }

  if (questionType === "MCQ" && !validateMCQ()) return;

  const payload = {
     id: editIndex,
    question_type: questionType,
    category,
    question_text: question,
    options: questionType === "MCQ" ? options : [],
  };

  // ---- IF EDITING A SAVED QUESTION ----
  if (currentEditingSavedIndex !== null) {
    await updateQuestion(payload, currentEditingSavedIndex);
    setCurrentEditingSavedIndex(null); // reset after updating
  } 
  // ---- ELSE: EDITING DRAFT OR NEW ----
  else {
    const updated = [...draftQuestions];
    if (editIndex !== null) {
      updated[editIndex] = payload;
    } else {
      updated.push(payload);
    }
    setDraftQuestions(updated);
    setEditIndex(null);
  }

  resetForm();
};


  const resetForm = () => {
    setQuestionType(null);
    setCategory(null);
    setQuestion("");
    setOptions(DEFAULT_OPTIONS);
    setEditIndex(null);
    setCurrentEditingSavedIndex(null);
  };

  /* ---------------- API: FETCH SAVED QUESTIONS ---------------- */
    const fetchSavedQuestions = async (questionType = null, category = null) => {
    const actionType = questionType || category ? "filter" : "get";

    const bodyData = {
        action: actionType,          // "get" by default, "filter" if any filter applied
        question_type: questionType, 
        category,                    
    };

    try {
        const res = await callApi({
        endpoint: API_URL,
        apiMethod: "post",
        bodyData,
        });

        if (res.decrypted?.status) {
        const questionsFromApi = res.decrypted.data.map((q) => ({
            id: q.id,
            question_type: q.question_type,
            category: q.category,
            question_text: q.question_text,
            options: q.pkt_options && q.pkt_options.length ? q.pkt_options : DEFAULT_OPTIONS,
            answer: q.answer || null,
            is_active: q.is_active,
        }));

        setSavedQuestions(questionsFromApi);
        } else {
        message.error("Failed to fetch saved questions");
        }
    } catch (err) {
        console.error(err);
        message.error("Failed to fetch saved questions");
    }
    };

  useEffect(() => {
  fetchSavedQuestions(filterQuestionType, filterCategory);
}, []);


  /* ---------------- API: SAVE ALL DRAFTS ---------------- */
    const handleSaveAllToDB = async () => {
    if (!draftQuestions.length) return;

    try {
        const res = await callApi({
        endpoint: API_URL,
        apiMethod: "post",
        bodyData: { action: "add", questions: draftQuestions },
        });

        const success = res?.status === true;

        if (success) {
        await fetchSavedQuestions(); 
        setDraftQuestions([]);
        message.success("Questions saved successfully");
        } else {
        console.error("Save API response:", res);
        message.error("Failed to save questions");
        }
    } catch (err) {
        console.error(err);
        message.error("Failed to save questions");
    }
    };

    /* ---------------- API: UPDATE SINGLE QUESTION ---------------- */
    const updateQuestion = async (questionPayload, index) => {
    const res = await callApi({
        endpoint: API_URL,
        apiMethod: "post",
        bodyData: {
        action: "update",
        questions: [questionPayload],
        },
    });
     const success = res?.status === true;
    if (success) {
        const updatedSaved = [...savedQuestions];
        updatedSaved[index] = questionPayload;
        setSavedQuestions(updatedSaved);
        message.success("Question updated successfully");
    } else {
        message.error("Failed to update question");
    }
    };
    /* ---------------- API: DELETE SINGLE QUESTION ---------------- */
    const deleteSavedQuestion = async (questionId, index) => {
    try {
        const res = await callApi({
        endpoint: API_URL,
        apiMethod: "post",
        bodyData: {
            action: "delete",
            id: questionId,
        },
        });

        const success = res?.status === true;
        console.log("KKKKKKKKKKKKKKKKKKKKKKKK : success : ",res)
        if (success) {
        const updated = [...savedQuestions];
        updated.splice(index, 1);
        setSavedQuestions(updated);
        message.success("Question deleted successfully");
        } else {
        message.error("Failed to delete question");
        }
    } catch (err) {
        console.error(err);
        message.error("Failed to delete question");
    }
    };


  /* ---------------- DELETE ---------------- */
    const handleDelete = () => {
    // Draft delete → UI only
    if (deleteFrom === "draft") {
        const updated = [...draftQuestions];
        updated.splice(deleteIndex, 1);
        setDraftQuestions(updated);
        return;
    }

    // Saved delete → API call
    if (deleteFrom === "saved") {
        const questionId = savedQuestions[deleteIndex]?.id;

        if (!questionId) {
        message.error("Invalid question id");
        return;
        }

        deleteSavedQuestion(questionId, deleteIndex);
    }
    };

    const fileInputRef = useRef(null);

  const handleExcelUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setSelectedExcelFile(file);
    setIsExcelConfirmOpen(true); 
    e.target.value = null; 
  };

  const confirmExcelUpload = async () => {
    if (!selectedExcelFile) return;

    const formData = new FormData();
    formData.append("file", selectedExcelFile);

    try {
      const res = await callApi({
        endpoint: "superadmin/master/upload-questions",
        apiMethod: "post",
        bodyData: formData,
        headers: { "Content-Type": "multipart/form-data" },
      });

      if (res?.status) {
        message.success("Excel uploaded successfully");
        await fetchSavedQuestions();
      } else {
        console.error("Upload API response:", res);
        message.error("Failed to upload Excel");
      }
    } catch (err) {
      console.error(err);
      message.error("Failed to upload Excel");
    } finally {
      setSelectedExcelFile(null);
      setIsExcelConfirmOpen(false);
    }
  };
  /* ---------------- PAGINATION ---------------- */
  const paginatedSaved = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return savedQuestions.slice(start, start + PAGE_SIZE);
  }, [savedQuestions, currentPage]);

  const formRef = useRef(null);
  return (
    <>
      {/* ================= ADD FORM ================= */}
    <Card
        ref={formRef}
        style={{ marginBottom: 24 }}
        title={
    <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
      <span
        style={{
          fontFamily: "Gilroy",
          fontWeight: 700,
          fontSize: "20px",
          marginTop: "10px",
          lineHeight: "28px",
          letterSpacing: "0px",
          color: "#000000",
        }}
      >
        Add/Update PKT Question
      </span>
      <span
        style={{
          fontFamily: "Gilroy",
          fontWeight: 400,
          fontSize: "14px",
          marginBottom: "10px",
          lineHeight: "20px",
          color: "#666666",
        }}
      >
        Create and manage questions.
      </span>
    </div>
  }
  extra={
    <Button
      type="primary"
      onClick={() => fileInputRef.current.click()}
      style={{
        background: "#008000", // green primary color
        border: "none",
        color: "#fff",
      }}
    >
      Upload Excel
    </Button>
  }
        >
          <input
          type="file"
          ref={fileInputRef}
          style={{ display: "none" }}
          accept=".xlsx,.xls"
          onChange={handleExcelUpload}
        />
        <div
          style={{
            display: "flex",
            gap: 16,
            width: "100%",
            marginBottom: 16,
          }}
        >
          <div style={{ flex: 1, minWidth: 200 }}>
            <label style={{ fontFamily: "Gilroy", fontWeight: 800, fontSize: "16px", lineHeight: "18px", letterSpacing: "0px", verticalAlign: "middle", color: "#000000", marginBottom: "8px", display: "block" }}>
                Select Question Type
            </label>

            <Select
              placeholder="Select Question Type"
              options={QUESTION_TYPES}
              value={questionType}
              onChange={setQuestionType}
              style={{ width: "100%",color: "#000000", }}
            />
          </div>

          <div style={{ flex: 1, minWidth: 200}}>
            <label style={{ fontFamily: "Gilroy", fontWeight: 800, fontSize: "16px", lineHeight: "18px", letterSpacing: "0px", verticalAlign: "middle", color: "#000000", marginBottom: "8px", display: "block" }}>
              Select Category
            </label>
            <Select
              placeholder="Select Category"
              options={CATEGORIES.map((c) => ({ label: c, value: c }))}
              value={category}
              onChange={setCategory}
              style={{ width: "100%",color: "#000000", }}
            />
          </div>
        </div>

    <div style={{ flex: 1, minWidth: 200 , marginTop: "18px"}}>
        <label style={{ fontFamily: "Gilroy", fontWeight: 800, fontSize: "16px", lineHeight: "18px", letterSpacing: "0px", verticalAlign: "middle", color: "#000000", marginBottom: "8px", display: "block" }}>
        Enter Question
        </label>
        <TextArea
          rows={3}
          placeholder="Enter question"
          value={question}
          onChange={(e) => setQuestion(e.target.value)}
          style={{ marginBottom: 4, color: "#000000", }}
        />
    </div>
        {questionType === "MCQ" && (
          <>
            <div style={{ fontFamily: "Gilroy", fontWeight: 700,fontSize: "20px",marginBottom :"10px", lineHeight: "28px", letterSpacing: "0px",color: "#000000",  }}>Add option (Select the correct answer)</div>
            {options.map((o, i) => (
              <Space key={i} style={{ display: "flex", marginBottom: 8, width: "100%" }}>
                <Radio checked={o.is_correct} onChange={() => setCorrectOption(i)} />
                <Input
                  value={o.option_text}
                  placeholder={`Option ${i + 1}`}
                  onChange={(e) => updateOptionText(i, e.target.value)}
                  style={{ flex: 1 }}
                />
              </Space>
            ))}
            <Button onClick={addOption}  icon={<PlusOutlined />} style={{ background: "var(--button-primary-bg, #000000)", height: "35px", padding: "12px", borderRadius: "4px", opacity: 1,marginTop: "6px", marginBottom: "6px", display: "flex", alignItems: "center", justifyContent: "center", gap: "10px", color: "#ffffff", border: "none" }}>
              Add More Option
            </Button>
          </>
        )}

        <Divider />

        <div style={{ display: "flex", justifyContent: "flex-end" }}>
          <Button
            type="primary"
            icon={<PlusOutlined />}
            onClick={handleAddDraft}
            style={{ background: "#052659" }}
          >
            Add Question
          </Button>
        </div>
      </Card>

      {/* ================= DRAFT QUESTIONS ================= */}
      {draftQuestions.length > 0 && (
        <Card style={{ marginBottom: 24 }}
        title={
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span
                style={{  fontFamily: "Gilroy", fontWeight: 700,fontSize: "20px",marginTop :"10px", lineHeight: "28px", letterSpacing: "0px",color: "#000000", }}>
                Draft Questions
            </span>
            <span
                style={{ fontFamily: "Gilroy", fontWeight: 400, fontSize: "14px", marginBottom :"10px",  lineHeight: "20px", color: "#666666", }}>
                View and save the questions.
            </span>
            </div>
        }>
        {draftQuestions.map((q, index) => (
        <div key={index}>
            <div
            className="pkt-question-row"
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
            }}
            >
            {/* Left side */}
            <div
                style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                flex: 1,
                }}
            >
                {/* Question text FIRST */}
                <span>{q.question_text}</span>

                {/* Question Type (label) */}
                <Tag color="blue">
                {getQuestionTypeLabel(q.question_type)}
                </Tag>

                {/* Category */}
                <Tag color="geekblue">{q.category}</Tag>
            </div>

            {/* Actions */}
            <Space>
                <Button
                size="small"
                onClick={() => {
                    setEditIndex(index);
                    setQuestionType(q.question_type);
                    setCategory(q.category);
                    setQuestion(q.question_text);
                    setOptions(q.options.length ? q.options : DEFAULT_OPTIONS);
                    formRef.current?.scrollIntoView({ behavior: "smooth" });
                }}
                >
                Edit
                </Button>

                <Button
                size="small"
                style={{ background: "#F44336", color: "#fff", border: "none" }}
                onClick={() => {
                    setDeleteIndex(index);
                    setDeleteFrom("draft");
                    setIsDeleteModalOpen(true);
                }}
                >
                Delete
                </Button>
            </Space>
            </div>

            <Divider style={{ margin: "6px 0", borderTop: "1px solid #eee" }} />
        </div>
        ))}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 12 }}>
            <Button
              type="primary"
              style={{ background: "#F44336", color: "#fff" }}
              onClick={() => {
                setDeleteFrom("draft-all");
                setIsDeleteModalOpen(true);
              }}
            >
              Delete All
            </Button>

            <Button
              type="primary"
              style={{ background: "#0b8457" }}
              icon={<SaveOutlined />}
              onClick={handleSaveAllToDB}
            >
              Save All Questions
            </Button>
          </div>
        </Card>
      )}

   {/* ================= SAVED QUESTIONS ================= */}
    <Card style={{ marginBottom: 24 }}
    title={
            <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
            <span
                style={{  fontFamily: "Gilroy", fontWeight: 700,fontSize: "20px",marginTop :"10px", lineHeight: "28px", letterSpacing: "0px",color: "#000000", }}>
                Saved Questions
            </span>
            <span
                style={{ fontFamily: "Gilroy", fontWeight: 400, fontSize: "14px", marginBottom :"10px",  lineHeight: "20px", color: "#666666", }}>
                View or edit the saved questions.
            </span>
            </div>
        }
    extra={
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <Select
          placeholder="Filter by Question Type"
          options={QUESTION_TYPES}
          value={filterQuestionType}
          onChange={(value) => {
            setFilterQuestionType(value);
            fetchSavedQuestions(value, filterCategory, "filter");
          }}
          style={{ minWidth: 180 }}
          allowClear
        />
        <Select
          placeholder="Filter by Category"
          options={CATEGORIES.map((c) => ({ label: c, value: c }))}
          value={filterCategory}
          onChange={(value) => {
            setFilterCategory(value);
            fetchSavedQuestions(filterQuestionType, value, "filter");
          }}
          style={{ minWidth: 180 }}
          allowClear
        />
        {savedQuestions.length > 0 && (
          <Button
            type="primary"
            style={{ background: "#F44336", color: "#fff", border: "none" }}
            onClick={() => {
              setDeleteFrom("saved-all");
              setIsDeleteModalOpen(true);
            }}
          >
            Delete All
          </Button>
        )}
      </div>
    }
    >
    {/* ---------------- QUESTIONS LIST ---------------- */}
    {paginatedSaved.length > 0 ? (
        paginatedSaved.map((q, index) => (
        <div key={q.id}>
            <div
            className="pkt-question-row"
            style={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                padding: "8px 0",
            }}
            >
            {/* LEFT SIDE: Question Text Only */}
            <div style={{ flex: 1 }}>{q.question_text}</div>

            {/* RIGHT SIDE: Action Buttons */}
            <Space>
                <Button
                    size="small"
                    onClick={() => {
                        setEditIndex(null); 
                        setCurrentEditingSavedIndex(index);
                        setQuestionType(q.question_type);
                        setCategory(q.category);
                        setQuestion(q.question_text);
                        setOptions(q.options.length ? q.options : DEFAULT_OPTIONS);
                        setEditIndex(q.id);
                        formRef.current?.scrollIntoView({ behavior: "smooth" });
                    }}
                    >
                    Edit
                    </Button>
                <Button
                size="small"
                style={{ background: "#F44336", color: "#fff", border: "none" }}
                onClick={() => {
                    setDeleteIndex(index);
                    setDeleteFrom("saved");
                    setIsDeleteModalOpen(true);
                }}
                >
                Delete
                </Button>
            </Space>
            </div>

            <Divider style={{ margin: "6px 0", borderTop: "1px solid #eee" }} />
        </div>
        ))
    ) : (
        <div style={{ textAlign: "center", padding: "16px 0", color: "#999" }}>
        No questions found
        </div>
    )}

    {/* ---------------- PAGINATION ---------------- */}
    {savedQuestions.length > PAGE_SIZE && (
        <Pagination
        current={currentPage}
        total={savedQuestions.length}
        pageSize={PAGE_SIZE}
        onChange={setCurrentPage}
        style={{ textAlign: "right", marginTop: 12,justifyContent: "flex-end"  }}
        />
    )}
    </Card>
      <DeleteModal
      open={isDeleteModalOpen}
      handleCancel={() => setIsDeleteModalOpen(false)}
      handleDelete={async () => {
        if (deleteFrom === "draft-all") {
          setDraftQuestions([]);
          message.success("All draft questions deleted");
        } else if (deleteFrom === "saved-all") {
          try {
            const res = await callApi({
              endpoint: API_URL,
              apiMethod: "post",
              bodyData: { action: "delete-all" },
            });

            if (res?.status) {
              setSavedQuestions([]);
              message.success("All saved questions deleted");
            } else {
              console.error("Delete All API response:", res);
              message.error("Failed to delete all saved questions");
            }
          } catch (err) {
            console.error(err);
            message.error("Failed to delete all saved questions");
          }
        } else {
          handleDelete();
        }
        setIsDeleteModalOpen(false);
      }}
      title={
        deleteFrom === "draft-all"
          ? "Are you sure you want to remove all the drafted questions?"
          : deleteFrom === "saved-all"
          ? "Are you sure you want to delete all the saved questions?"
          : "Are you sure you want to remove this question?"
      }
      description={
        deleteFrom === "draft-all"
          ? "These drafted questions will be removed from the list. This action can’t be undone."
          : deleteFrom === "saved-all"
          ? "These saved questions will be deleted from the Questionnaire. This action can’t be undone."
          : "This question will be removed. This action can’t be undone."
      }
    />
    <ConfirmModal
        open={isExcelConfirmOpen}
        handleCancel={() => {
          setIsExcelConfirmOpen(false);
          setSelectedExcelFile(null);
        }}
        handleConfirm={confirmExcelUpload}
        title="Are you sure you want to upload this excel sheet?"
        description="Are you sure you want to upload this Excel file? This will add questions to the questionnaire. This action can’t be undone."
        confirmButtonText="Upload"
        cancelButtonText="Cancel"
        icon="/assets/upload-icon.png"
      />
    </>
  );
};

export default PktQuestionnaire;
